import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalContext } from "@/context/GlobalContext";
import { useBrand, findBoardTag } from "@/lib/brand-context";
import { BoardTag, Keyword, BOARD_CONFIG } from "@/types";
import { ChevronLeft, Search, Upload, ChevronDown, Check, X } from "lucide-react";

const SOURCES = [
  { key: "seo",     label: "SEO Seeds",      desc: "Organic search keywords" },
  { key: "sem",     label: "SEM",             desc: "Paid search keywords" },
  { key: "opinion", label: "Public Opinion",  desc: "Trend & social keywords" },
] as const;

interface ImportResult {
  totalProcessed: number;
  addedCount: number;
  duplicateCount: number;
}

export default function StrategyHubPage() {
  const navigate = useNavigate();
  const { keywords, setKeywords } = useGlobalContext();
  const { config } = useBrand();
  const boardTags: BoardTag[] = config.boardTags && config.boardTags.length > 0
    ? config.boardTags
    : Object.entries(BOARD_CONFIG).map(([k, v]) => ({ key: k, ...v }));

  const [activeSource, setActiveSource] = useState<"seo" | "sem" | "opinion">("seo");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Import dropdown
  const [dropOpen, setDropOpen] = useState(false);
  const [importSrc, setImportSrc] = useState<"seo" | "sem" | "opinion">("seo");

  // Import modal
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importBoard, setImportBoard] = useState(boardTags[0]?.key ?? "edukasi");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  const filtered = keywords.filter((kw) => {
    const matchesSource = kw.source === activeSource;
    const matchesSearch =
      !search ||
      kw.keyword.toLowerCase().includes(search.toLowerCase()) ||
      kw.raw.toLowerCase().includes(search.toLowerCase());
    return matchesSource && matchesSearch;
  });

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((k) => k.id)));
    }
  }

  function handleGenerateSelected() {
    const selectedKws = keywords.filter((k) => selected.has(k.id));
    if (selectedKws.length === 0) return;
    const kwParam = selectedKws.map((k) => encodeURIComponent(k.keyword)).join(",");
    // Use the most common board among selected keywords (fallback: first row's board)
    const boardCounts: Record<string, number> = {};
    selectedKws.forEach(k => { boardCounts[k.intentBoard] = (boardCounts[k.intentBoard] ?? 0) + 1; });
    const board = Object.entries(boardCounts).sort((a, b) => b[1] - a[1])[0][0];
    navigate(`/ai-creator?keywords=${kwParam}&board=${board}`);
  }

  function openImport(src: "seo" | "sem" | "opinion") {
    setImportSrc(src);
    setImportText("");
    setImportResult(null);
    setImportOpen(true);
    setDropOpen(false);
  }

  async function handleConfirmImport() {
    const lines = importText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;

    setImporting(true);
    await new Promise((r) => setTimeout(r, 800));

    const existingSet = new Set(keywords.map((k) => k.keyword.toLowerCase()));
    const toAdd: Keyword[] = [];
    let duplicates = 0;

    lines.forEach((line) => {
      if (existingSet.has(line.toLowerCase())) {
        duplicates++;
      } else {
        existingSet.add(line.toLowerCase());
        toAdd.push({
          id: `k_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          raw: line,
          keyword: line,
          intentBoard: importBoard,
          source: importSrc,
          createdAt: new Date().toISOString().split("T")[0],
        });
      }
    });

    setKeywords((prev) => [...prev, ...toAdd]);
    const result: ImportResult = {
      totalProcessed: lines.length,
      addedCount: toAdd.length,
      duplicateCount: duplicates,
    };
    setImportResult(result);
    setImporting(false);
    showToast(`Imported ${toAdd.length} keywords (${duplicates} duplicates skipped)`);
  }

  function getBoardDisplay(tag: BoardTag) {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tag.bg} ${tag.text}`}>
        {tag.label}
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Top Nav */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft size={20} />
        </Link>
        <h1 className="text-lg font-semibold text-gray-900">Strategy Hub</h1>
        <div className="ml-auto flex items-center gap-3">
          {/* Import Dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropOpen(!dropOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              <Upload size={14} />
              Import Keywords
              <ChevronDown size={13} />
            </button>
            {dropOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20">
                {SOURCES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => openImport(s.key)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-sm font-medium text-gray-800">{`Import to ${s.label}`}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{s.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Generate Selected */}
          {selected.size > 0 && (
            <button
              onClick={handleGenerateSelected}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Generate Selected ({selected.size})
            </button>
          )}
        </div>
      </div>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Sidebar */}
        <div className="w-52 bg-white border-r border-gray-100 p-4 flex flex-col gap-1 flex-shrink-0">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 px-2">SOURCE</p>
          {SOURCES.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveSource(s.key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                activeSource === s.key
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {s.label}
              <span className="ml-2 text-xs text-gray-400">
                ({keywords.filter((k) => k.source === s.key).length})
              </span>
            </button>
          ))}
        </div>

        {/* Main */}
        <div className="flex-1 overflow-auto p-6">
          {/* Search row */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search keywords..."
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <span className="text-sm text-gray-400">{filtered.length} keywords</span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Keyword</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Board</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vol</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Diff</th>
                  <th className="w-24 px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-400 text-sm">
                      No keywords found. Import some to get started.
                    </td>
                  </tr>
                )}
                {filtered.map((kw) => {
                  const tag = findBoardTag(boardTags, kw.intentBoard);
                  return (
                    <tr
                      key={kw.id}
                      className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${
                        selected.has(kw.id) ? "bg-blue-50/40" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(kw.id)}
                          onChange={() => toggleSelect(kw.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{kw.keyword}</div>
                        {kw.raw !== kw.keyword && (
                          <div className="text-xs text-gray-400 mt-0.5">{kw.raw}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{getBoardDisplay(tag)}</td>
                      <td className="px-4 py-3 text-gray-600">{kw.volume?.toLocaleString() ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${
                          (kw.difficulty ?? 0) > 60 ? "text-red-500" : (kw.difficulty ?? 0) > 40 ? "text-amber-500" : "text-green-500"
                        }`}>
                          {kw.difficulty ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            navigate(`/ai-creator?keywords=${encodeURIComponent(kw.keyword)}&board=${kw.intentBoard}`);
                          }}
                          className="text-xs px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-medium transition-colors"
                        >
                          Create Article
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {importOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Import to {SOURCES.find((s) => s.key === importSrc)?.label}
              </h3>
              <button onClick={() => setImportOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {!importResult ? (
              <>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Assign to Board</label>
                  <select
                    value={importBoard}
                    onChange={(e) => setImportBoard(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {boardTags.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Keywords (one per line)
                  </label>
                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    rows={8}
                    placeholder={"pinjaman online aman\nkredit usaha kecil\ncara lapor pinjol ilegal"}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none font-mono"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setImportOpen(false)}
                    className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmImport}
                    disabled={importing || !importText.trim()}
                    className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
                  >
                    {importing ? "Importing..." : "Import"}
                  </button>
                </div>
              </>
            ) : (
              <div>
                <div className="bg-green-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-3">
                    <Check size={16} />
                    Import Complete
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white rounded-lg p-2">
                      <div className="text-xl font-bold text-gray-800">{importResult.totalProcessed}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Processed</div>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <div className="text-xl font-bold text-green-600">{importResult.addedCount}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Added</div>
                    </div>
                    <div className="bg-white rounded-lg p-2">
                      <div className="text-xl font-bold text-amber-500">{importResult.duplicateCount}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Skipped</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setImportOpen(false); setImportResult(null); }}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-5 py-3 rounded-2xl shadow-xl z-50">
          {toast}
        </div>
      )}

      {/* Close dropdown on outside click */}
      {dropOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setDropOpen(false)} />
      )}
    </div>
  );
}
